import React, { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

function parseJwt(token) {
  try {
    const base64 = token.split(".")[1];
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

function isExpired(token) {
  const payload = parseJwt(token);
  if (!payload?.exp) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSec;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => localStorage.getItem("token") || "");

  const [impersonatedOrg, setImpersonatedOrg] = useState(() => {
    try { return JSON.parse(localStorage.getItem("impersonatedOrg")) || null; } catch { return null; }
  });

  useEffect(() => {
    if (user) localStorage.setItem("user", JSON.stringify(user));
    else localStorage.removeItem("user");

    if (token) localStorage.setItem("token", token);
    else localStorage.removeItem("token");
  }, [user, token]);

  useEffect(() => {
    if (token && isExpired(token)) {
      setUser(null);
      setToken("");
    }
  }, [token]);

  const login = (userData, tokenData) => {
    // Écriture synchrone en localStorage avant le re-render pour que
    // l'intercepteur axios trouve le token dès le premier appel API post-login.
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", tokenData);
    setUser(userData);
    setToken(tokenData);
  };

  const logout = () => {
    ["user", "token", "orig_token", "orig_user", "impersonatedOrg"].forEach((k) => localStorage.removeItem(k));
    setUser(null);
    setToken("");
    setImpersonatedOrg(null);
  };

  // Superadmin « entre » dans une org : bascule sur un jeton d'impersonation en
  // conservant le jeton superadmin d'origine pour pouvoir revenir.
  const impersonate = (newToken, org) => {
    if (!localStorage.getItem("orig_token")) {
      localStorage.setItem("orig_token", localStorage.getItem("token") || "");
      localStorage.setItem("orig_user", localStorage.getItem("user") || "");
    }
    const payload = parseJwt(newToken) || {};
    const impUser = {
      id: payload.id,
      username: payload.username,
      role: payload.role,
      org_id: payload.org_id,
      permissions: [],
      impersonating: true,
    };
    localStorage.setItem("token", newToken);
    localStorage.setItem("user", JSON.stringify(impUser));
    localStorage.setItem("impersonatedOrg", JSON.stringify(org));
    setToken(newToken);
    setUser(impUser);
    setImpersonatedOrg(org);
  };

  const stopImpersonation = () => {
    const origToken = localStorage.getItem("orig_token");
    const origUser = localStorage.getItem("orig_user");
    localStorage.removeItem("orig_token");
    localStorage.removeItem("orig_user");
    localStorage.removeItem("impersonatedOrg");
    if (origToken) { localStorage.setItem("token", origToken); setToken(origToken); }
    else localStorage.removeItem("token");
    if (origUser) { localStorage.setItem("user", origUser); setUser(JSON.parse(origUser)); }
    setImpersonatedOrg(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, impersonate, stopImpersonation, impersonatedOrg, impersonating: !!impersonatedOrg }}>
      {children}
    </AuthContext.Provider>
  );
};
